import { useEffect, useState, useCallback } from 'react';
import { AdditionalDevelopment } from "../../Models/AdditionalDevelopment";
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Grid,
  CircularProgress,
  Box,
  TextField,
  Stack,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  School as SchoolIcon,
  AssignmentRounded as AssignmentRoundedIcon,
  VideoLibraryRounded as VideoLibraryRoundedIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Moment from 'moment';
import { Moment as MomentType } from 'moment';
import { visuallyHidden } from '@mui/utils';
import { DocumentCompleted } from '../../Models/DocumentCompleted';
import { StreamCompleted } from '../../Models/StreamCompleted';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Snackbar from '@mui/material/Snackbar';
import { useUserContext } from '../../Context/UserContext';
import { useAPIContext } from '../../Context/APIContext';
import { DevelopmentCard } from './DevelopmentCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdditionalDevelopmentUI() {
  const [submissionTitle, setSubmissionTitle] = useState('');
  const [submissionText, setSubmissionText] = useState('');
  const [submissionDate, setSubmissionDate] = useState<MomentType | null>(null);
  const [additionalDevelopment, setAdditionalDevelopment] = useState<AdditionalDevelopment[]>([]);
  const [completedStreams, setCompletedStreams] = useState<StreamCompleted[]>([]);
  const [completedDocuments, setCompletedDocuments] = useState<DocumentCompleted[]>([]);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();
  const { fetchPoints } = useUserContext();
  const { apiFetch } = useAPIContext();
  const theme = useTheme();

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleAddAdditionalDevelopment = async () => {
    setAwaitingResponse(true);

    const date = submissionDate?.toDate();
    const submission = new AdditionalDevelopment(
      0,
      0,
      new Date(),
      submissionTitle,
      5,
      date,
      submissionText
    );

    const response = await apiFetch<number>('Users/AdditionalDevelopment', 'POST', submission);

    if (response && response > 0) {
      submission.id = response;
      setAdditionalDevelopment(prev => [...prev, submission]);
      setSnackBarOpen(true);

      setSubmissionTitle('');
      setSubmissionText('');
      setSubmissionDate(null);

      fetchPoints();
    }

    setAwaitingResponse(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [devData, streamsData, docsData] = await Promise.all([
        apiFetch<AdditionalDevelopment[]>('Users/AdditionalDevelopment', 'GET', AdditionalDevelopment.fromJsonArray),
        apiFetch<StreamCompleted[]>('Users/CompletedStreams', 'GET', StreamCompleted.fromJsonArray),
        apiFetch<DocumentCompleted[]>('Users/CompletedDocuments', 'GET', DocumentCompleted.fromJsonArray),
      ]);

      setAdditionalDevelopment(devData || []);
      setCompletedStreams(streamsData || []);
      setCompletedDocuments(docsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStreamClick = (streamId: number) => {
    navigate(`/stream/${streamId}`);
  };

  const handleDocumentClick = (documentId: number) => {
    navigate(`/document/${documentId}`);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ maxWidth: '1600px', mx: 'auto', px: { xs: 2, md: 4 }, py: 3 }}>
      <Typography component="h1" style={visuallyHidden}>
      Professional Digital Portfolio
      </Typography>

      <Stack spacing={4}>
        <Box>
          <Typography
            sx={{
              fontSize: '1.5em',
              [theme.breakpoints.down('sm')]: {
                fontSize: theme.typography.h6.fontSize,
              },
            }}
          >
            Professional Digital Portfolio
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your learning progress and submit additional development activities
          </Typography>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress aria-label="Loading activities" />
          </Box>
        ) : (
          <>
            {/* Quick Stats */}
            <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
              <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                <Paper sx={{ p: 2, textAlign: 'center', flex: 1, mb: { xs: 2, sm: 0 } }} elevation={3}>
                  <Typography variant="h6" color="text.secondary">Videos Completed</Typography>
                  <Typography variant="h3" color="primary">{completedStreams.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                <Paper sx={{ p: 2, textAlign: 'center', flex: 1, mb: { xs: 2, sm: 0 } }} elevation={3}>
                  <Typography variant="h6" color="text.secondary">How-to Guides Completed</Typography>
                  <Typography variant="h3" color="primary">{completedDocuments.length}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
                <Paper sx={{ p: 2, textAlign: 'center', flex: 1 }} elevation={3}>
                  <Typography variant="h6" color="text.secondary">Additional Development Activities</Typography>
                  <Typography variant="h3" color="primary">{additionalDevelopment.length}</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Activity Feed */}
            <Box>
              <Typography variant="h6" className="header-text">Activity</Typography>

              <Stack spacing={2} sx={{ width: '100%' }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="tabs" variant="scrollable" scrollButtons="auto">
                  <Tab label="Completed Videos" {...a11yProps(0)} />
                  <Tab label="Completed How-to Guides" {...a11yProps(1)} />
                  <Tab label="Additional Development Activities" {...a11yProps(2)} />
                </Tabs>

                {/* Button to open modal */}
                {tabValue === 2 && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleOpenModal}
                    sx={{
                      fontWeight: 600,
                      py: 1,
                      color: '#FFF'
                    }}
                  >
                    Add Development Activity <AddIcon sx={{ ml: 1 }} />
                  </Button>
                </Box>)}


                
                {/* Modal for new submission */}
                <Dialog open={openModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
                  <DialogTitle sx={{ fontWeight: 500, pb: 1 }}>
                    New Development Activity
                  </DialogTitle>

                  {/* Subtle separator */}
                  <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }} />

                  <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="Date of Development"
                        value={submissionDate}
                        onChange={(newValue) => setSubmissionDate(newValue)}
                      />
                    </LocalizationProvider>

                    <TextField
                      fullWidth
                      label="Your title"
                      variant="outlined"
                      value={submissionTitle}
                      onChange={(e) => setSubmissionTitle(e.target.value)}
                    />

                    {/* Character tracker field */}
                    {(() => {
                      const MIN_CHARS = 200;
                      const charCount = submissionText.length;
                      const hasText = charCount > 0;
                      const isTooShort = hasText && charCount < MIN_CHARS;

                      return (
                        <TextField
                          fullWidth
                          multiline
                          minRows={4}
                          label="Describe your development"
                          variant="outlined"
                          value={submissionText}
                          onChange={(e) => setSubmissionText(e.target.value)}
                          error={isTooShort}
                          helperText={
                            hasText
                              ? `${charCount} / ${MIN_CHARS} characters`
                              : `Minimum ${MIN_CHARS} characters`
                          }
                          sx={{
                            '& .MuiInputBase-root': {
                              lineHeight: 1.6,
                              backgroundColor: '#fafafa',
                            },
                          }}
                        />
                      );
                    })()}
                  </DialogContent>

                  <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleCloseModal} variant="text" color="inherit" sx={{ fontWeight: 550 }}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: theme.palette.primary.main, color: '#FFF', fontWeight: 550 }}
                      disabled={
                        !submissionTitle ||
                        !submissionText ||
                        submissionText.length < 200 ||
                        !submissionDate ||
                        awaitingResponse
                      }
                      onClick={async () => {
                        await handleAddAdditionalDevelopment();
                        handleCloseModal();
                      }}
                    >
                      {awaitingResponse ? 'Submitting...' : 'Submit'}
                    </Button>
                  </DialogActions>
                </Dialog>



                <CustomTabPanel value={tabValue} index={2}>
                  {additionalDevelopment.length > 0 ? (
                    additionalDevelopment.map((dev) => (
                      <DevelopmentCard
                        key={dev.id}
                        type="development"
                        title={dev.title}
                        date={Moment(dev.dateAndTimeSubmitted).format('MMMM D, YYYY')}
                        completedDate={dev.dateAndTimeCompleted ? Moment(dev.dateAndTimeCompleted).format('MMMM D, YYYY') : undefined}
                        description={dev.submissionText ?? ""}
                        points={dev.points ?? 0}
                        icon={<SchoolIcon />}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      No additional developments found.
                    </Typography>
                  )}
                </CustomTabPanel>

                <CustomTabPanel value={tabValue} index={0}>
                  {completedStreams.length > 0 ? (
                    completedStreams.map((stream) => (
                      <DevelopmentCard
                        key={stream.id}
                        type="video"
                        title={stream.streamTitle}
                        date={Moment(stream.dateAndTime).format('MMMM D, YYYY')}
                        description={stream.submissionText ?? ""}
                        points={stream.points ?? 0}
                        onClick={() => handleStreamClick(stream.streamId)}
                        icon={<VideoLibraryRoundedIcon />}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">No completed videos found.</Typography>
                  )}
                </CustomTabPanel>

                <CustomTabPanel value={tabValue} index={1}>
                  {completedDocuments.length > 0 ? (
                    completedDocuments.map((document) => (
                      <DevelopmentCard
                        key={document.id}
                        type="document"
                        title={document.documentTitle}
                        date={Moment(document.dateAndTime).format('MMMM D, YYYY')}
                        description={document.submissionText ?? ""}
                        points={document.points ?? 0}
                        onClick={() => handleDocumentClick(document.documentId)}
                        icon={<AssignmentRoundedIcon />}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="textSecondary">No completed how-to guides found.</Typography>
                  )}
                </CustomTabPanel>
              </Stack>
            </Box>
          </>
        )}
      </Stack>

      {/* Snackbar */}
      <Snackbar open={snackBarOpen} autoHideDuration={6000} onClose={() => setSnackBarOpen(false)}>
        <Alert onClose={() => setSnackBarOpen(false)} variant="filled" sx={{ width: '100%', backgroundColor: '#66bb6a' }}>
          Submission Successful
        </Alert>
      </Snackbar>
    </Box>
  );
}
